import { Toast } from '@base-ui/react/toast';
import { cn } from '@foundation/utils/cn';
import { ComponentPropsWithoutRef, ReactNode } from 'react';

import { ensurePortalRoot } from '../primitives/ensurePortalRoot';

export type {
  ToastManagerAddOptions as HeadlessToastAddOptions,
  ToastManagerUpdateOptions as HeadlessToastUpdateOptions,
  ToastObject as HeadlessToastObject,
} from '@base-ui/react/toast';
export const useToastManager = Toast.useToastManager;

export type HeadlessToastProviderProps = ComponentPropsWithoutRef<
  typeof Toast.Provider
>;

// Orchestrator: owns the toast queue/timers. Must wrap the part of the app that calls useToastManager().
function Provider(props: HeadlessToastProviderProps) {
  return <Toast.Provider {...props} />;
}

export type HeadlessToastViewportProps = {
  readonly children: ReactNode;
  readonly className?: string;
  readonly portalId?: string;
  readonly 'data-testid'?: string;
};

// Portals the toast stack to a dedicated DOM node under <body>, mirroring Modal/Drawer.
function Viewport({
  children,
  className,
  portalId = 'pmi-toast-root',
  'data-testid': dataTestId,
}: HeadlessToastViewportProps) {
  return (
    <Toast.Portal container={ensurePortalRoot(portalId)}>
      <Toast.Viewport
        className={cn('fixed', className)}
        data-testid={dataTestId}
      >
        {children}
      </Toast.Viewport>
    </Toast.Portal>
  );
}

export type HeadlessToastRootProps = ComponentPropsWithoutRef<
  typeof Toast.Root
>;

// One entry in the queue; carries its own swipe-to-dismiss and enter/exit transition state.
function Root(props: HeadlessToastRootProps) {
  return <Toast.Root {...props} />;
}

export type HeadlessToastContentProps = ComponentPropsWithoutRef<
  typeof Toast.Content
>;

// Fades out for toasts sitting behind the frontmost one while the stack is collapsed.
function Content(props: HeadlessToastContentProps) {
  return <Toast.Content {...props} />;
}

export type HeadlessToastTitleProps = ComponentPropsWithoutRef<
  typeof Toast.Title
>;

function Title(props: HeadlessToastTitleProps) {
  return <Toast.Title {...props} />;
}

export type HeadlessToastDescriptionProps = ComponentPropsWithoutRef<
  typeof Toast.Description
>;

function Description(props: HeadlessToastDescriptionProps) {
  return <Toast.Description {...props} />;
}

export type HeadlessToastActionProps = ComponentPropsWithoutRef<
  typeof Toast.Action
>;

function Action(props: HeadlessToastActionProps) {
  return <Toast.Action {...props} />;
}

export type HeadlessToastCloseProps = ComponentPropsWithoutRef<
  typeof Toast.Close
>;

function Close(props: HeadlessToastCloseProps) {
  return <Toast.Close {...props} />;
}

export const HeadlessToast = {
  Provider,
  Viewport,
  Root,
  Content,
  Title,
  Description,
  Action,
  Close,
};
